import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { paymentsApi } from "@/api";
import type { RefundStatus } from "@/api";
import { QUERY_KEYS } from "@/constants";

/** Navbat shu oraliqda yangilanadi — yangi chek kelganda sahifani yangilash shart emas. */
const QUEUE_REFRESH_MS = 30_000;

export const useCardReviewQueue = (filter: { page: number; limit: number }) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_CARD_REVIEW, filter],
    queryFn: () => paymentsApi.cardReviewQueue(filter),
    placeholderData: keepPreviousData,
    refetchInterval: QUEUE_REFRESH_MS,
    select: res => ({ rows: res.data, meta: res.meta }),
  });

export const useCardHistory = (filter: {
  page: number;
  limit: number;
  status?: "PAID" | "REJECTED";
}) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_CARD_HISTORY, filter],
    queryFn: () => paymentsApi.cardHistory(filter),
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
  });

const useInvalidateCard = () => {
  const queryClient = useQueryClient();
  return () => {
    for (const key of [
      QUERY_KEYS.GET_CARD_REVIEW,
      QUERY_KEYS.GET_CARD_HISTORY,
      QUERY_KEYS.GET_PAYMENT_LEDGER,
    ]) {
      queryClient.invalidateQueries({ queryKey: [key] });
    }
  };
};

export const useApproveCardPayment = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidateCard();
  return useMutation({
    mutationFn: (id: number) => paymentsApi.approveCard(id),
    onSuccess: () => {
      invalidate();
      toast.success(t("CARD_REVIEW.APPROVED_TOAST"));
    },
  });
};

export const useRejectCardPayment = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidateCard();
  return useMutation({
    mutationFn: (vars: { id: number; reason: string }) =>
      paymentsApi.rejectCard(vars.id, vars.reason),
    onSuccess: () => {
      invalidate();
      toast.success(t("CARD_REVIEW.REJECTED_TOAST"));
    },
  });
};

// ─── Pulni qaytarish so'rovlari ──────────────────────────────────────────────

export const useRefundRequests = (filter: { page: number; limit: number; status?: RefundStatus }) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_REFUND_REQUESTS, filter],
    queryFn: () => paymentsApi.refundRequests(filter),
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
  });

const useInvalidateRefunds = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_REFUND_REQUESTS] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_PAYMENT_LEDGER] });
  };
};

export const useCompleteRefund = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidateRefunds();
  return useMutation({
    mutationFn: (vars: { id: number; note?: string }) =>
      paymentsApi.completeRefund(vars.id, vars.note),
    onSuccess: () => {
      invalidate();
      toast.success(t("REFUND_REQUESTS.COMPLETED_TOAST"));
    },
  });
};

export const useRejectRefund = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidateRefunds();
  return useMutation({
    mutationFn: (vars: { id: number; reason: string }) =>
      paymentsApi.rejectRefund(vars.id, vars.reason),
    onSuccess: () => {
      invalidate();
      toast.success(t("REFUND_REQUESTS.REJECTED_TOAST"));
    },
  });
};
